import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";

import { links } from "@/data/links";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

import { DataList13FData } from "ui/13f/data-list-13f/DataList13FProps";
import { getFilteredData } from "ui/13f/data-list-13f/utils";
import { IconInput } from "ui/core/input";
import { OutlineAccountIcon } from "ui/icon/shared";

export const SearchDialogContent = ({
  closeDialog,
  data,
}: {
  closeDialog: () => void;
  data: DataList13FData[];
}) => {
  const router = useRouter();
  const { t, lang } = useLang();
  const [query, setQuery] = useState("");
  const filteredData = useMemo(
    () => (data ? getFilteredData({ data, query, lang }) : []),
    [data, lang, query]
  );

  const handleSelect = (investor: string | undefined) => {
    if (investor) router.push(`/${lang}${links.my13FDetail({ investor })}`);
    closeDialog();
  };

  return (
    <div className="flex h-[calc(100%-56px)] w-full flex-col items-center gap-5 pb-6">
      <div className="w-full px-4">
        <IconInput
          value={query}
          placeholder={t.my13F.overview.search13F}
          aria-label="search 13F institution"
          className="text-sm"
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          leadingIcon={
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          }
        />
      </div>
      <div className="w-full divide-y-[1px] overflow-y-auto scrollbar-hide">
        {filteredData?.map((item) => {
          return (
            <MenuItem
              key={item.investor}
              data={item}
              lang={lang}
              onClick={() => handleSelect(item.investor)}
            />
          );
        })}
      </div>
    </div>
  );
};

const MenuItem = ({
  data,
  lang,
  onClick,
}: {
  data: DataList13FData;
  lang: Lang;
  onClick: () => void;
}) => (
  <div
    className="flex cursor-pointer select-none flex-col gap-y-1 px-6 py-3 transition-colors hover:bg-sky-100"
    onClick={onClick}
    role="listitem"
    aria-label="13F institution"
  >
    <p className="relative flex items-center gap-2 whitespace-nowrap">
      <span className="text-sm font-medium">{data.investorName}</span>
      {lang === "zh" ? (
        <span className="truncate text-xs">{data.investorNameEn}</span>
      ) : null}
    </p>
    <p className="flex items-center gap-x-2 text-xs">
      <OutlineAccountIcon />
      <span className="text-neutral-500">{data.representative}</span>
    </p>
  </div>
);
